import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type MarcoActivityEntry = {
  id: string;
  kind: "door_assigned" | "delta_detected" | "enrichment";
  occurredAt: string;
  title: string;
  detail: string | null;
  actionable?: boolean;
};

/**
 * Merges mentorax.lead_doors, delta_audit_log, and enrichment_records for
 * one contact into a single chronological feed — the design specified in
 * docs/prd/marco-activity-feed-design.md, unblocked 2026-09-04 once
 * lead_doors/delta_audit_log were corrected from uuid to bigint contact_id
 * (see the same doc's "Blocker found" section, now resolved).
 *
 * scraped_place_signals is deliberately excluded — it's company/location
 * level (no contact_id at all), belongs on a future Company activity feed,
 * not here.
 */
export function useMarcoActivity(contactId: number | undefined) {
  return useQuery({
    queryKey: ["marco-activity", contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<MarcoActivityEntry[]> => {
      const mentorax = getSupabaseClient().schema("mentorax");

      const [doorsRes, deltasRes, enrichmentsRes] = await Promise.all([
        mentorax
          .from("lead_doors")
          .select("id, door_category, confidence, rationale, assigned_at")
          .eq("contact_id", contactId),
        mentorax
          .from("delta_audit_log")
          .select("id, trigger_type, delta_summary, actionable_signal, created_at")
          .eq("contact_id", contactId),
        mentorax
          .from("enrichment_records")
          .select("id, summary, confidence_score, created_at")
          .eq("contact_id", contactId),
      ]);

      for (const res of [doorsRes, deltasRes, enrichmentsRes]) {
        if (res.error) throw res.error;
      }

      const entries: MarcoActivityEntry[] = [
        ...(doorsRes.data ?? []).map((d) => ({
          id: `door-${d.id}`,
          kind: "door_assigned" as const,
          occurredAt: d.assigned_at,
          title: `Door assigned: ${d.door_category ?? "unknown"}`,
          detail: [d.confidence && `confidence: ${d.confidence}`, d.rationale]
            .filter(Boolean)
            .join(" — ") || null,
        })),
        ...(deltasRes.data ?? []).map((d) => ({
          id: `delta-${d.id}`,
          kind: "delta_detected" as const,
          occurredAt: d.created_at,
          title: `Signal detected: ${d.trigger_type ?? "change"}`,
          detail: d.delta_summary,
          actionable: d.actionable_signal ?? false,
        })),
        ...(enrichmentsRes.data ?? []).map((e) => ({
          id: `enrich-${e.id}`,
          kind: "enrichment" as const,
          occurredAt: e.created_at,
          title: "Enriched",
          detail: e.summary,
        })),
      ];

      return entries.sort(
        (a, b) =>
          new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      );
    },
  });
}
